import React, {memo, useCallback} from "react";
import {Avatar, Flex, LeaveIcon, MenuButton, Skeleton, Text} from "@fluentui/react-northstar";
import {IUserPreviewProps} from "./UserPreview.interfaces";
import {IGraphUserData} from "services/GraphService";
import {useTranslation} from "services/TranslationService/TranslationService.hook";
import {useUserPreview} from "./UserPreview.hook";
import {IUser} from "Interfaces/IUser";

const propsAreEqual = (prevProps: Readonly<IUserPreviewProps>, nextProps: Readonly<IUserPreviewProps>): boolean => {
    return (
        prevProps.userId === nextProps.userId
    )
}

export const UserPreview = memo((props: IUserPreviewProps) => {
    const translate = useTranslation();
    const logic = useUserPreview(props);

    const renderMenuButton = useCallback((user: IGraphUserData, trigger: JSX.Element) => {
        return (
            <Flex className={"cursor-pointer"}>
                <MenuButton
                    className={"no-select"}
                    trigger={trigger}
                    on={"click"}
                    menu={{
                        styles: {marginTop: "10px"},
                        items: [
                            {
                                key: "user-name",
                                content: (
                                    <Text
                                        styles={{color: logic.isLightTheme ? "black" : "white"}}
                                        content={user.displayName}
                                        weight={"semibold"}
                                    />
                                ),
                                disabled: true,
                            },
                            {
                                key: "disconnect",
                                content: translate("SignOut"),
                                icon: <LeaveIcon outline/>,
                                onClick: logic.handleDisconnect,
                            }
                        ]
                    }}
                />
            </Flex>
        )
    }, [logic.isLightTheme]);

    const renderUnknownUserPreview = useCallback(() => (
        <Flex className={"w-100"} vAlign={"center"} gap={"gap.small"}>
            <Avatar className={"no-select"} label={"?"}/>
        </Flex>
    ), []);

    const renderUserPreview = useCallback((user: IUser, isCurrentUser: boolean) => (
        <Flex className={"w-100 " + (!isCurrentUser ? "cursor-pointer" : "")} vAlign={"center"} gap={"gap.small"}>
            <Flex>
                <Avatar
                    size={props.size ?? "medium"}
                    className={"no-select"}
                    image={user.picture}
                    name={user.displayName}
                />
            </Flex>
        </Flex>
    ), []);

    const renderSkeleton = useCallback((userId: string) => (
        <Skeleton key={"user-preview-skeleton-" + userId} animation="pulse">
            <Flex vAlign={"center"} gap={"gap.small"}>
                <Skeleton.Shape round width="32px" height="32px"/>
            </Flex>
        </Skeleton>
    ), []);

    if (!logic.userId) return renderUnknownUserPreview();
    if (!logic.user) return renderSkeleton(logic.userId);
    let preview: JSX.Element;
    preview = renderUserPreview(logic.user!, logic.isCurrentUser);
    return renderMenuButton(logic.user!, preview);
}, propsAreEqual)